import { useCallback } from "react";
import { useForm } from "react-hook-form";
import type {
  Control,
  FieldErrors,
  UseFormHandleSubmit,
  UseFormSetValue,
  UseFormTrigger,
} from "react-hook-form";

import type { HttpError } from "src-next/shared/api/httpClient";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";
import type { MessageTemplate } from "../types";
import type { MessageTemplateFormValues } from "../formTypes";

export interface UseMessageTemplateFormFlowOptions {
  initialValues?: MessageTemplateFormValues;
  onSubmitTemplate: (values: MessageTemplateFormValues) => Promise<void>;
}

export interface UseMessageTemplateFormFlowResult {
  control: Control<MessageTemplateFormValues>;
  formErrors: FieldErrors<MessageTemplateFormValues>;
  handleSubmit: ReturnType<UseFormHandleSubmit<MessageTemplateFormValues>>;
  isSubmitting: boolean;
  hasSubmitError: boolean;
  resetForm: () => void;
  fillFromTemplate: (template: MessageTemplate) => void;
  triggerFieldValidation: (fieldName: keyof MessageTemplateFormValues) => void;
  setValue: UseFormSetValue<MessageTemplateFormValues>;
  trigger: UseFormTrigger<MessageTemplateFormValues>;
}

const DEFAULT_FORM_VALUES: MessageTemplateFormValues = {
  name: "",
  header: "",
  body: "",
  hasButton: false,
  buttonType: "URL",
  buttonText: "",
  buttonValue: "",
};

const DUPLICATE_NAME_ERROR_MESSAGE = "Já existe um template com esse nome para este idioma/conta";

function isNameConflictError(error: unknown): boolean {
  const httpError = error as HttpError | undefined;
  const status = httpError?.status;
  const message = String(httpError?.message ?? "").toLowerCase();
  const dataText = JSON.stringify(httpError?.data ?? "").toLowerCase();

  const hasNameConflictMessage =
    message.includes("já existe") ||
    message.includes("duplicate") ||
    message.includes("already exists") ||
    dataText.includes("já existe") ||
    dataText.includes("duplicate") ||
    dataText.includes("already exists");

  return status === 409 || hasNameConflictMessage;
}

export function useMessageTemplateFormFlow({
  initialValues,
  onSubmitTemplate,
}: UseMessageTemplateFormFlowOptions): UseMessageTemplateFormFlowResult {
  const defaultValues = initialValues ?? DEFAULT_FORM_VALUES;
  const {
    control,
    handleSubmit: reactHookFormHandleSubmit,
    formState: { errors },
    reset,
    setValue,
    setError,
    clearErrors,
    trigger,
  } = useForm<MessageTemplateFormValues>({
    mode: "onBlur",
    defaultValues,
  });

  const { run, loading: isSubmitting, error } = useAsyncTask<
    [MessageTemplateFormValues],
    void
  >({
    task: useCallback(
      async (values: MessageTemplateFormValues) => {
        await onSubmitTemplate(values);
      },
      [onSubmitTemplate],
    ),
  });

  const handleSubmit = reactHookFormHandleSubmit(async (values) => {
    clearErrors("name");

    try {
      await run(values);
    } catch (error) {
      if (isNameConflictError(error)) {
        setError("name", {
          type: "server",
          message: DUPLICATE_NAME_ERROR_MESSAGE,
        });
        return;
      }
      throw error;
    }
  });

  const resetForm = useCallback(() => {
    reset(DEFAULT_FORM_VALUES);
  }, [reset]);

  const fillFromTemplate = useCallback(
    (template: MessageTemplate) => {
      reset({
        name: template.name,
        header: template.header ?? "",
        body: template.preview,
        hasButton: Boolean(template.buttonText),
        buttonType: template.buttonType as "URL",
        buttonText: template.buttonText ?? "",
        buttonValue: template.buttonValue ?? "",
      });
    },
    [reset],
  );

  const triggerFieldValidation = useCallback(
    (fieldName: keyof MessageTemplateFormValues) => {
      void trigger(fieldName);
    },
    [trigger],
  );

  return {
    control,
    formErrors: errors,
    handleSubmit,
    isSubmitting,
    hasSubmitError: Boolean(error) && !Boolean(errors.name),
    resetForm,
    fillFromTemplate,
    triggerFieldValidation,
    setValue,
    trigger,
  };
}
